import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faSolidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import API_ENDPOINTS, { IMAGE_BASE_URL } from '../../api/apiConfig';
import { useUser } from "../../context/UserContext";
import 'swiper/css';
import 'swiper/css/navigation';
import LoadingSpinner from '../LoadingSpinner';
import type { Product } from '../../types';
import '../../assets/css/theme.css';
import '../../assets/css/landing.css';

const BestSellers = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const { user } = useUser();
  const token = localStorage.getItem('authToken');

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(API_ENDPOINTS.RANDOM_PRODUCTS, { method: 'POST', headers });
      const data = await response.json();
      if (response.ok && Array.isArray(data.data)) setProducts(data.data);
      else setError(data.message || 'Failed to load products.');
    } catch {
      setError('An error occurred while fetching products.');
    }
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, [token]);

  const handleWishlistToggle = async (productId: number) => {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(API_ENDPOINTS.WISHLISTTOGGLE, {
        method: "POST", headers,
        body: JSON.stringify({ product_id: productId }),
      });
      const data = await response.json();
      if (response.ok) fetchProducts();
      else alert(data.message || "Failed to update wishlist.");
    } catch (err) {
      console.error("Wishlist toggle error:", err);
    }
  };

  const renderCard = (product: Product, idx = 0) => {
    const originalPrice   = parseFloat(String(product.p_price)) || 0;
    const discountPct     = parseFloat(String(product.discount_percentage)) || 0;
    const discountedPrice = Math.round(originalPrice * (1 - discountPct / 100));
    const isVendor        = !!token && user?.created_by;
    const productUrl      = `/product/${product.p_slug}`;
    const firstImage      = product.images?.[0];
    const imageSrc        = firstImage?.startsWith("http") ? firstImage : `${IMAGE_BASE_URL}${firstImage}`;

    return (
      <article
        className="ffh-pc"
        key={product.p_id}
        style={{ animationDelay: `${idx * 0.06}s` }}
      >
        <div className="ffh-pc-media">
          <a href={productUrl} className="ffh-pc-link" aria-label={product.p_name}>
            <img src={imageSrc} alt={product.p_name} loading="lazy" />
          </a>

          {discountPct > 0 && <span className="ffh-pc-badge">−{discountPct}%</span>}

          {token && (
            <button
              type="button"
              className={`ffh-pc-wish${product.is_wishlisted ? ' is-active' : ''}`}
              onClick={() => handleWishlistToggle(product.p_id)}
              aria-label={product.is_wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <FontAwesomeIcon icon={product.is_wishlisted ? faSolidHeart : faRegularHeart} />
            </button>
          )}

          <a href={productUrl} className="ffh-pc-quick">View product</a>
        </div>

        <div className="ffh-pc-body">
          <a href={productUrl} className="ffh-pc-name">{product.p_name}</a>
          <div className="ffh-pc-price">
            {isVendor ? (
              <>
                <span className="ffh-pc-now">₹{discountedPrice.toLocaleString('en-IN')}</span>
                {discountPct > 0 && (
                  <span className="ffh-pc-orig">₹{originalPrice.toLocaleString('en-IN')}</span>
                )}
              </>
            ) : (
              <span className="ffh-pc-now">₹{originalPrice.toLocaleString('en-IN')}</span>
            )}
          </div>
        </div>
      </article>
    );
  };

  const showSlider = !loading && !error && products.length >= 4;

  return (
    <section className="ffh-pc-sec ffh-pc-sec--cream">
      <div className="ffh-container">

        {/* ── Section Header ── */}
        <div className="ffh-pc-head">
          <div>
            <p className="ffh-eyebrow ffh-eyebrow--left">Most Loved</p>
            <h2 className="ffh-title">Best <em>Sellers</em></h2>
          </div>

          <div className="ffh-pc-tools">
            <a href="/products" className="ffh-pc-all">
              View all
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            {showSlider && (
              <>
                <button type="button" className="ffh-round-btn ffh-best-prev" aria-label="Previous">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M11 14L6 9l5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button type="button" className="ffh-round-btn ffh-best-next" aria-label="Next">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M7 4l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="ffh-error">{error}</div>
        ) : products.length === 0 ? (
          <div className="ffh-empty">No products found.</div>
        ) : products.length < 4 ? (
          <div className="ffh-pc-static">
            {products.map((p, i) => renderCard(p, i))}
          </div>
        ) : (
          <div className="ffh-pc-slider">
            <Swiper
              modules={[Navigation]}
              navigation={{ nextEl: '.ffh-best-next', prevEl: '.ffh-best-prev' }}
              spaceBetween={24}
              grabCursor={true}
              speed={600}
              loop={true}
              slidesPerView={1.15}
              breakpoints={{
                480:  { slidesPerView: 2, spaceBetween: 18 },
                768:  { slidesPerView: 2, spaceBetween: 20 },
                992:  { slidesPerView: 3, spaceBetween: 22 },
                1200: { slidesPerView: 4, spaceBetween: 26 },
              }}
              style={{ padding: '8px 4px 12px' }}
            >
              {products.map((p, i) => (
                <SwiperSlide key={p.p_id} style={{ height: 'auto' }}>
                  {renderCard(p, i)}
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
};

export default BestSellers;